/* ---------------------------------------------------------------------------
   HEADER ACCOUNT MENU

   The account control opens a small panel: sign in and register when signed
   out, My Account and Sign Out when signed in. It borrows the .syn-panel shell
   from the wish list and bag overlays and, like them, is positioned from
   script: the panel is fixed rather than absolute, because the header is a grid
   of flex rows and any ancestor with clipping or its own stacking context would
   hide a panel anchored inside it.

   Opening follows the same rules as those two overlays -- hover on a desktop
   mouse with a short close delay, tap to toggle on a touch tablet -- so all
   three header panels behave identically.
   --------------------------------------------------------------------------- */
(function () {
  /* A hover-capable, fine pointer means a desktop mouse. Touch tablets report
     (hover: none), so they keep tap-to-open rather than opening on a stray
     pointer event. */
  const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  class SynergyAccountMenu extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-syn-account-toggle]');
      this.panel = this.querySelector('[data-syn-account-panel]');
      if (!this.trigger || !this.panel) return;

      /* The <li> is what carries the fill-while-open state, so the icon stays
         filled the whole time the panel is up. */
      this.item = this.closest('.syn-acctmenu') || this;
      this.href = this.trigger.getAttribute('data-syn-account-href');

      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        /* Desktop: hover already shows the panel, so a click goes to the page
           behind it. Touch tablet: no hover, so the tap toggles the panel. */
        if (canHover() && this.href) {
          window.location.href = this.href;
          return;
        }
        this.panel.hidden ? this.open() : this.close();
      });

      this.addEventListener('mouseenter', () => { if (canHover()) { this.cancelClose(); this.open(); } });
      this.addEventListener('mouseleave', () => { if (canHover()) this.scheduleClose(); });
      this.panel.addEventListener('mouseenter', () => this.cancelClose());
      this.panel.addEventListener('mouseleave', () => { if (canHover()) this.scheduleClose(); });

      /* Capture phase: a click on a link inside the panel must still resolve,
         so the outside-click test asks whether the target is ours first. */
      document.addEventListener('click', (e) => {
        if (this.contains(e.target)) return;
        this.close();
      }, true);

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      window.addEventListener('resize', () => { if (!this.panel.hidden) this.place(); });
      window.addEventListener('scroll', () => { if (!this.panel.hidden) this.place(); }, { passive: true });
    }

    cancelClose() { window.clearTimeout(this.hoverTimer); }

    /* Long enough to cross the gap between the icon and the panel below it. */
    scheduleClose() {
      this.cancelClose();
      this.hoverTimer = window.setTimeout(() => this.close(), 260);
    }

    place() {
      const r = this.trigger.getBoundingClientRect();
      const width = this.panel.offsetWidth || 200;
      const gap = 8;

      const wantTop = Math.round(r.bottom + gap);
      let wantEnd = Math.round(Math.max(8, window.innerWidth - r.right));
      if (window.innerWidth - wantEnd - width < 8) {
        wantEnd = Math.max(8, window.innerWidth - width - 8);
      }

      this.panel.style.top = wantTop + 'px';
      this.panel.style.insetInlineEnd = wantEnd + 'px';

      const got = this.panel.getBoundingClientRect();
      const driftY = Math.round(got.top - wantTop);
      const driftX = Math.round(window.innerWidth - got.right - wantEnd);
      if (driftY) this.panel.style.top = wantTop - driftY + 'px';
      if (driftX) this.panel.style.insetInlineEnd = wantEnd - driftX + 'px';
    }

    open() {
      if (!this.panel.hidden) return;          // already open; hover re-fires a lot
      this.panel.hidden = false;
      this.place();
      requestAnimationFrame(() => this.panel.classList.add('is-open'));
      this.trigger.setAttribute('aria-expanded', 'true');
      this.item.classList.add('is-panel-open');
    }

    close() {
      this.cancelClose();
      if (this.panel.hidden) return;
      this.panel.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.item.classList.remove('is-panel-open');
      const done = () => { this.panel.hidden = true; };
      this.panel.addEventListener('transitionend', done, { once: true });
      window.setTimeout(done, 250);
    }
  }

  if (!customElements.get('synergy-account-menu')) {
    customElements.define('synergy-account-menu', SynergyAccountMenu);
  }
})();
